
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem,
  useSidebar
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';
import { NavigationItem } from './navigationItems';

interface SidebarNavGroupProps {
  title: string;
  items: NavigationItem[];
  className?: string;
  activeColorClass?: string;
}

const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({ 
  title, 
  items, 
  className = '', 
  activeColorClass = 'text-blue-600' 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile } = useSidebar();
  const isCollapsed = state === 'collapsed' && !isMobile;

  const isActiveRoute = (route: string) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const handleClick = (item: NavigationItem) => {
    if (item.mvpActive === false) {
      navigate(`/coming-soon?feature=${encodeURIComponent(item.title)}`);
    } else {
      navigate(item.route);
    }
  };

  if (items.length === 0) return null;

  return (
    <SidebarGroup className={className}>
      {title && (
        <SidebarGroupLabel className="text-xs font-medium text-muted-foreground px-3 mb-1">
          {!isCollapsed ? title : ''}
        </SidebarGroupLabel>
      )}
      <SidebarGroupContent>
        <SidebarMenu className="space-y-0.5">
          {items.map((item) => {
            const isMuted = item.mvpActive === false;
            return (
              <SidebarMenuItem key={item.route}>
                <SidebarMenuButton
                  isActive={!isMuted && isActiveRoute(item.route)}
                  onClick={() => handleClick(item)}
                  tooltip={isCollapsed ? item.title : undefined}
                  className={`
                    h-9 transition-colors
                    ${isMuted ? 'opacity-40' : ''}
                    ${!isMuted && isActiveRoute(item.route)
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'hover:bg-accent/50'
                    }
                    ${isCollapsed ? 'justify-center px-0' : 'justify-start px-3'}
                  `}
                >
                  <item.icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4 mr-3 flex-shrink-0'}`} />
                  {!isCollapsed && (
                    <span className="text-sm truncate max-w-full flex-1">
                      {item.title}
                    </span>
                  )}
                  {!isCollapsed && isMuted && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 ml-auto border-muted-foreground/30 text-muted-foreground/60">
                      Soon
                    </Badge>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

export default SidebarNavGroup;
